import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import ComplianceStatusBadge from '../../admin-dashboard/components/ComplianceStatusBadge';
import AssignSiteManagerPanel from './AssignSiteManagerPanel';
import ProgressBar from '../../../components/ui/ProgressBar';
import { activityService } from '../../../services/activityService';
import { useLanguage } from '../../../contexts/LanguageContext';

const OverviewTab = ({ siteData, documents, incidents, audits, snags, checklists, tasks, taskCounts, userRole, onRefresh, onTabChange }) => {
  const { t } = useLanguage();
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  useEffect(() => {
    if (siteData?.id) {
      loadRecentActivities(siteData?.id);
    }
  }, [siteData?.id]);

  const loadRecentActivities = async (siteId) => {
    setActivitiesLoading(true);
    const { data, error: err } = await activityService?.getActivitiesBySite(siteId, { limit: 5 });
    if (err) {
      console.error('[OverviewTab] recent activities failed:', { code: err?.code, message: err?.message });
    }
    setRecentActivities(data || []);
    setActivitiesLoading(false);
  };

  const getActivityIcon = (type) => {
    const icons = {
      task_completed: 'CheckCircle2',
      snag_created: 'AlertCircle',
      snag_resolved: 'CheckCircle',
      snag_updated: 'Edit',
      document_uploaded: 'FileText',
      photo_evidence_added: 'Camera',
      worker_assigned: 'UserPlus',
      site_created: 'Building2',
      site_updated: 'Settings',
      incident_created: 'AlertTriangle',
    };
    return icons?.[type] || 'Activity';
  };

  const getActivityColor = (type) => {
    const colors = {
      task_completed: 'var(--color-success)',
      snag_created: 'var(--color-warning)',
      snag_resolved: 'var(--color-success)',
      snag_updated: 'var(--color-accent)',
      document_uploaded: 'var(--color-accent)',
      photo_evidence_added: 'var(--color-accent)',
      worker_assigned: 'var(--color-success)',
      site_created: 'var(--color-accent)',
      site_updated: 'var(--color-accent)',
      incident_created: 'var(--color-destructive)',
    };
    return colors?.[type] || 'var(--color-muted-foreground)';
  };

  const getRelativeTime = (dateStr) => {
    const now = Date.now();
    const then = new Date(dateStr)?.getTime();
    const diff = Math.floor((now - then) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(dateStr)?.toLocaleDateString();
  };

  const criticalIncidents = incidents?.filter(i => i?.severity_level === 'critical' || i?.severity_level === 'high')?.length || 0;
  const expiringSoonDocs = documents?.filter(d => d?.status === 'expiring-soon' || d?.status === 'expiring-critical')?.length || 0;
  const completedAudits = audits?.filter(a => a?.status === 'completed')?.length || 0;

  const totalSnags = snags?.length || 0;
  const resolvedSnags = snags?.filter(s => s?.status === 'resolved')?.length || 0;
  const snagResolutionRate = totalSnags > 0 ? Math.round((resolvedSnags / totalSnags) * 100) : 0;

  const totalChecklists = checklists?.length || audits?.length || 0;
  const completedChecklists = checklists?.filter(c => c?.status === 'completed')?.length ?? completedAudits;
  const checklistRate = totalChecklists > 0 ? Math.round((completedChecklists / totalChecklists) * 100) : 0;

  // Prefer parent-supplied counts (covers all dates for the site). Fall back
  // to the in-memory tasks array for callers that haven't migrated yet.
  const totalTasks = taskCounts?.total ?? (tasks?.length || 0);
  const completedTasks = taskCounts?.completed ?? (tasks?.filter(t => t?.status === 'completed' || t?.is_completed)?.length || 0);
  const taskRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const siteCompletion = (() => {
    if (!siteData?.start_date || !siteData?.end_date) return siteData?.completion_pct ?? null;
    const start = new Date(siteData.start_date)?.getTime();
    const end = new Date(siteData.end_date)?.getTime();
    const now = Date.now();
    if (now <= start) return 0;
    if (now >= end) return 100;
    return Math.round(((now - start) / (end - start)) * 100);
  })();

  const summaryCards = [
    {
      title: t('complianceStatus'),
      value: <ComplianceStatusBadge status={siteData?.compliance_status} />,
      icon: 'ShieldCheck',
      color: 'var(--color-success)'
    },
    {
      title: t('totalDocuments'),
      value: documents?.length || 0,
      icon: 'FileText',
      color: 'var(--color-accent)'
    },
    {
      title: t('criticalIncidents'),
      value: criticalIncidents,
      icon: 'AlertTriangle',
      color: 'var(--color-destructive)'
    },
    {
      title: t('completedAudits'),
      value: completedAudits,
      icon: 'ClipboardCheck',
      color: 'var(--color-success)'
    }
  ];

  const urgentIssues = [
    ...(expiringSoonDocs > 0 ? [{
      id: 'docs',
      title: t('documentsExpiringSoon'),
      description: `${expiringSoonDocs} ${t('documentRequireAttention')}`,
      icon: 'FileText',
      color: 'var(--color-warning)'
    }] : []),
    ...(criticalIncidents > 0 ? [{
      id: 'incidents',
      title: t('criticalIncidents'),
      description: `${criticalIncidents} ${t('criticalIncidentReported')}`,
      icon: 'AlertTriangle',
      color: 'var(--color-destructive)'
    }] : [])
  ];

  // Schedule Progress + Work Completed are always shown side-by-side so the
  // distinction Heidi asked for is visible: time elapsed vs. work done.
  // Snag/Checklist bars stay conditional — they're not the headline pair.
  const progressItems = [
    ...(siteCompletion !== null ? [{ label: t('siteTimelineProgress'), value: siteCompletion, icon: 'Building2' }] : []),
    {
      label: totalTasks > 0
        ? `${t('siteWorkCompleted')} (${completedTasks}/${totalTasks})`
        : t('siteWorkCompleted'),
      value: taskRate,
      icon: 'ListTodo',
      placeholder: totalTasks === 0 ? t('noTasksYet') : null,
    },
    ...(totalSnags > 0 ? [{ label: `${t('snagResolution')} (${resolvedSnags}/${totalSnags})`, value: snagResolutionRate, icon: 'Wrench' }] : []),
    ...(totalChecklists > 0 ? [{ label: `${t('checklistCompletion')} (${completedChecklists}/${totalChecklists})`, value: checklistRate, icon: 'ClipboardCheck' }] : []),
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards?.map((card) => (
          <div key={card?.title} className="bg-muted/50 rounded-lg p-4 border border-border">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 bg-background rounded-lg">
                <Icon name={card?.icon} size={20} color={card?.color} />
              </div>
              <p className="text-sm text-muted-foreground">{card?.title}</p>
            </div>
            <div className="text-2xl font-bold text-foreground">{card?.value}</div>
          </div>
        ))}
      </div>
      {/* Progress Bars Section */}
      {progressItems?.length > 0 && (
        <div className="bg-card rounded-lg border border-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="TrendingUp" size={18} className="text-accent" />
            <h3 className="text-base font-semibold text-foreground">{t('siteProgressOverview')}</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {progressItems?.map((item) => (
              <div key={item?.label} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name={item?.icon} size={15} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  {item?.placeholder ? (
                    <div className="w-full">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-foreground">{item?.label}</span>
                        <span className="text-sm font-medium text-muted-foreground">{item?.placeholder}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2.5" />
                    </div>
                  ) : (
                    <ProgressBar
                      value={item?.value}
                      label={item?.label}
                      height="h-2.5"
                      color="var(--color-accent)"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Admin-only: Assign Site Manager */}
      {userRole === 'admin' && siteData && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">{t('siteManagement')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AssignSiteManagerPanel siteData={siteData} onAssigned={onRefresh} />
          </div>
        </div>
      )}
      {urgentIssues?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">{t('urgentIssues')}</h3>
          <div className="space-y-3">
            {urgentIssues?.map((issue) => (
              <div key={issue?.id} className="bg-muted/50 rounded-lg p-4 border border-border flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-background rounded-lg flex-shrink-0">
                  <Icon name={issue?.icon} size={20} color={issue?.color} />
                </div>
                <div>
                  <h4 className="font-medium text-foreground mb-1">{issue?.title}</h4>
                  <p className="text-sm text-muted-foreground">{issue?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {urgentIssues?.length === 0 && (
        <div className="bg-success/10 rounded-lg p-6 border border-success/20 text-center">
          <Icon name="CheckCircle2" size={48} className="text-success mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('allClear')}</h3>
          <p className="text-sm text-muted-foreground">{t('noUrgentIssues')}</p>
        </div>
      )}
      {/* Recent Activity Feed */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="Activity" size={18} className="text-accent" />
            <h3 className="text-base font-semibold text-foreground">{t('recentActivity')}</h3>
          </div>
          <button
            onClick={() => onTabChange?.('timeline')}
            className="text-xs text-accent hover:underline flex items-center gap-1"
          >
            {t('viewAllActivity')}
            <Icon name="ArrowRight" size={12} />
          </button>
        </div>

        {activitiesLoading ? (
          <div className="flex items-center justify-center py-6">
            <Icon name="Loader2" size={20} className="text-muted-foreground animate-spin" />
          </div>
        ) : recentActivities?.length === 0 ? (
          <div className="text-center py-6">
            <Icon name="Activity" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{t('noActivityYet')}</p>
          </div>
        ) : (
          <div className="space-y-0">
            {recentActivities?.map((activity, index) => (
              <div key={activity?.id} className="flex items-start gap-3 py-3 relative">
                {index < recentActivities?.length - 1 && (
                  <div className="absolute left-4 top-10 bottom-0 w-px bg-border" />
                )}
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-muted z-10">
                  <Icon
                    name={getActivityIcon(activity?.activity_type)}
                    size={14}
                    color={getActivityColor(activity?.activity_type)}
                  />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-sm text-foreground leading-snug">{activity?.description || activity?.title}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {activity?.user?.full_name && (
                      <span className="text-xs text-muted-foreground">{activity?.user?.full_name}</span>
                    )}
                    {activity?.user?.full_name && (
                      <span className="text-xs text-muted-foreground">·</span>
                    )}
                    <span className="text-xs text-muted-foreground">{getRelativeTime(activity?.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;